import { useMemo } from "react";
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  format,
  isAfter,
  parseISO,
  startOfDay,
} from "date-fns";
import type { Color } from "@salestrak/ui";
import { NO_OUTCOME_KEY } from "@salestrak/widgets";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { useScopedActivityWhere } from "#app/widgets/dashboards/scope/use-scoped-activity-where";
import { useEffectiveScope } from "#app/widgets/dashboards/scope/scope-shared";
import { resolveDatePreset, useWeekStartsOn } from "#app/lib/utils/date-presets";
import { useWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import {
  outcomeSeriesGroupBy,
  timeSeriesGroupBy,
  useMemberEntityResolver,
  type TimeSeriesDimension,
  type TimeSeriesGrain,
  type TimeSeriesMeasure,
} from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { normalizeOutcomeTone, OUTCOME_TONE_CONFIG } from "#app/lib/outcome-tone";
import { UNASSIGNED_DIMENSION_KEY } from "#app/widgets/dashboards/use-dimension-counts";
import type { FieldMeasure } from "#app/widgets/dashboards/widget-measure";
import { useActivityGroupCounts } from "../shared/use-activity-group-counts";
import { at } from "#app/lib/utils/at";

/** One shared empty ask, so an unmeasured card's query variables never churn. */
const NO_MEASURES: readonly FieldMeasure[] = [];

/** Which timestamp seeds the buckets. */
export type SeriesMeasure = TimeSeriesMeasure;
/** Bucket granularity along the time axis. A card stores only week or month;
 *  `auto` picks `day` for a window of two weeks or less, so "This Week" plots
 *  each day rather than one week point. */
export type SeriesBucket = TimeSeriesGrain;
/** The longest window (in days) `auto` plots by day. */
const DAY_BUCKET_MAX_SPAN = 14;
/** The Interval a card stores: a bucket, or `auto` for the window-sized one. */
export const SERIES_GRAINS = ["auto", "week", "month"] as const;
export type SeriesGrain = (typeof SERIES_GRAINS)[number];
/** Split the count into one line per dimension value, or a single total line.
 *  `outcome` is the one split that counts Updates rather than projects. `team`
 *  is the Assigned Team — sent as the server's `teams` dimension. */
export type SeriesGroupBy = Exclude<TimeSeriesDimension, "teams"> | "outcome" | "none";

/** One point on a line — the bucket start (ms, for stable ordering + the band
 *  x value), its axis label, the plotted value (per-bucket count or field
 *  aggregate, or a running total of either), and the projects behind it. */
export type LinePoint = { t: number; label: string; y: number; count: number };
/** One line — a stage (or the single "Projects" total) and its zero-filled points. */
/** One drawn line: the split entity's id/label, its own palette color (charts
 * wear it; `null` for the total line and unassigned buckets), and the points. */
export type LineSeriesData = { id: string; label: string; color: Color | null; points: LinePoint[] };

/** At most this many stage lines; the rest fold away so the chart stays readable. */
const MAX_SERIES = 5;

/**
 * A time-series reader for the line card. It reuses the SAME server aggregation
 * the count cards use — `useIssueGroupCounts` — asking it to group by a date
 * bucket (`<measure>.<grain>`, or `stage.<measure>.<grain>` for one line per
 * stage) over the shared scope + Filters fold. So the plot is exact per-period
 * counts computed in Postgres, not a client-side sample: it stays accurate no
 * matter how large the table or how lumpy the data (bulk imports included).
 * The returned buckets are zero-filled and continuous between the first and last
 * populated period so a gap reads as zero, not a skipped tick; `cumulative`
 * turns the per-bucket counts into a running total. Under field measures
 * (`fieldMeasures`, see `widget-measure.ts`) each point plots the period's
 * aggregate instead of its count; a running total then applies only to a sum
 * — an accumulated average or extreme is not a figure — and a period whose
 * projects hold no value plots as zero, the line's honest gap. SEVERAL fields
 * draw one line each (the field's id names the line, the field's name labels
 * it) over the unsplit total, since a split multiplied by the fields would be
 * unreadable — the card passes `groupBy` "none" with them. `pending` (a
 * "Custom field" Measure with no field yet) asks nothing. An `outcome` split
 * reads the ACTIVITY aggregation instead (`outcome.<grain>` over the scoped
 * activity fold): one line per outcome, each point the Updates posted with it
 * in the period — Updates carry no field, so the measures are ignored, and the
 * date is the Update's own. `grain` is the card's Interval row: a fixed week
 * or month bucket, or `auto`, which reads the window — days up to two weeks, weeks up to a quarter,
 * months beyond. A card NEVER re-plumbs the scope fold — it calls this.
 */
export function useSeriesLine(
  measure: SeriesMeasure,
  groupBy: SeriesGroupBy,
  cumulative: boolean,
  filters: unknown,
  fieldMeasures: readonly FieldMeasure[] = NO_MEASURES,
  pending = false,
  grain: SeriesGrain = "auto",
) {
  const { base, override } = useWidgetFilters(filters);
  const { effDateRange } = useEffectiveScope(override);
  const weekStartsOn = useWeekStartsOn();
  const range = useMemo(
    () => resolveDatePreset(effDateRange.key, effDateRange.custom),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the preset reads the week start from module state
    [effDateRange.key, effDateRange.custom, weekStartsOn],
  );
  const bucket = useMemo<SeriesBucket>(() => {
    if (grain !== "auto") return grain;
    const spanDays = differenceInCalendarDays(new Date(range.endDate), new Date(range.startDate));
    if (spanDays <= DAY_BUCKET_MAX_SPAN) return "day";
    return spanDays <= 92 ? "week" : "month";
  }, [grain, range]);
  const dateField = measure === "updated" ? ("lastUpdateDate" as const) : ("creationDate" as const);
  const { where } = useScopedWhere(base, { dateField, override });
  const outcome = groupBy === "outcome";
  // BOTH readers mount every render — hook order is fixed, so the split picks a
  // RESULT, never which hook runs. The unused one costs no query: issue counts
  // skip on an undefined `groupBy`, activity counts on their own `skip`.
  // Same aggregation the Metric / Pie cards call — just a date-bucket groupBy.
  const groupToken = timeSeriesGroupBy(
    measure,
    bucket,
    groupBy === "none" || outcome ? undefined : groupBy === "team" ? "teams" : groupBy,
  );
  const issueCounts = useIssueGroupCounts({
    where,
    groupBy: pending || outcome ? undefined : groupToken,
    measures: outcome ? NO_MEASURES : fieldMeasures,
  });
  const { where: activityWhere } = useScopedActivityWhere(null, { override });
  const outcomeCounts = useActivityGroupCounts({
    groupBy: outcomeSeriesGroupBy(bucket),
    where: activityWhere,
    skip: !outcome || pending,
  });
  const { counts, ready, error } = outcome ? outcomeCounts : issueCounts;
  // The outcome series carries no field figures; an empty map keeps the fold
  // one shape (memoized, so the series memo below keys on a stable object).
  const values = useMemo(() => (outcome ? {} : issueCounts.values), [outcome, issueCounts.values]);
  const fn = outcome ? null : (fieldMeasures[0]?.fn ?? null);
  const accumulate = cumulative && (fn === null || fn === "sum");
  const { stageMap, issueTypeMap, workflowMap, teamMap, issueSourceMap, reasons, customFields } =
    useWorkspaceData();
  // Assignee keys resolve through the directory (a member OR a team), the same
  // way the count cards name them.
  const resolveMember = useMemberEntityResolver();
  const reasonMap = useMemo(
    () => new Map(reasons.map((reason) => [reason.reasonId, reason])),
    [reasons],
  );

  const series = useMemo<LineSeriesData[]>(() => {
    const addFn = bucket === "month" ? addMonths : bucket === "week" ? addWeeks : addDays;
    // Labels carry the year so buckets stay unique across years (a year-less
    // "MMM d" collapsed multi-year weeks onto one another). The axis renders a
    // compacted form; this fuller label is the tooltip title.
    const fmt = bucket === "month" ? "MMM yyyy" : "MMM d, yyyy";

    // `counts` keys: total → "YYYY-MM-DD"; split → "<dimensionId|__unassigned__>|YYYY-MM-DD".
    // Several fields fan one bucket out into one cell per field, the field id
    // naming the line.
    const severalFields = fieldMeasures.length > 1;
    const cells = Object.entries(counts)
      .flatMap(([key, count]) => {
        let seriesId = "__all__";
        let dateStr = key;
        if (groupBy !== "none") {
          const sep = key.lastIndexOf("|");
          seriesId = sep >= 0 ? key.slice(0, sep) : UNASSIGNED_DIMENSION_KEY;
          dateStr = sep >= 0 ? key.slice(sep + 1) : key;
        }
        const date = parseISO(dateStr);
        if (Number.isNaN(date.getTime())) return [];
        const figures = values[key] ?? [];
        if (severalFields) {
          return fieldMeasures.map((fieldMeasure, index) => ({
            seriesId: fieldMeasure.fieldId,
            dateStr,
            date,
            y: figures[index] ?? 0,
            count,
          }));
        }
        return [{ seriesId, dateStr, date, y: fn ? (figures[0] ?? 0) : count, count }];
      })
      .filter(
        (c): c is { seriesId: string; dateStr: string; date: Date; y: number; count: number } =>
          c != null,
      );

    if (cells.length === 0) return [];

    // Continuous, zero-filled bucket axis from the earliest to the latest period.
    const firstCell = at(cells, 0);
    let min = firstCell.date;
    let max = firstCell.date;
    for (const c of cells) {
      if (c.date < min) min = c.date;
      if (c.date > max) max = c.date;
    }
    // A day axis spans the whole window up to today, so a quiet day still
    // reads as zero at either end (a week or month axis can reach back to
    // the epoch under "All time", so it keeps to the populated periods).
    if (bucket === "day") {
      const windowStart = startOfDay(new Date(range.startDate));
      const windowEnd = startOfDay(
        new Date(Math.min(new Date(range.endDate).getTime(), Date.now())),
      );
      if (windowStart < min) min = windowStart;
      if (windowEnd > max) max = windowEnd;
    }
    const axis: { key: string; t: number; label: string }[] = [];
    for (let d = min; !isAfter(d, max); d = addFn(d, 1)) {
      axis.push({ key: format(d, "yyyy-MM-dd"), t: d.getTime(), label: format(d, fmt) });
    }
    const indexByKey = new Map(axis.map((b, i) => [b.key, i]));

    // The line's name: the split entity's, or the empty bucket's noun.
    const labelOf = (id: string): string => {
      if (severalFields) {
        return customFields.find((field) => field.customFieldId === id)?.name ?? "Unknown field";
      }
      const missing = id === UNASSIGNED_DIMENSION_KEY;
      switch (groupBy) {
        case "stage":
          return missing ? "No stage" : (stageMap.get(id)?.name ?? "Unknown stage");
        case "type":
          return missing ? "No type" : (issueTypeMap.get(id)?.name ?? "Unknown type");
        case "workflow":
          return missing ? "No workflow" : (workflowMap.get(id)?.name ?? "Unknown workflow");
        case "source":
          return missing ? "No source" : (issueSourceMap.get(id)?.name ?? "Unknown source");
        case "team":
          return missing ? "No team" : (teamMap.get(id)?.teamName ?? "Unknown team");
        case "assignee": {
          if (missing) return "Unassigned";
          const member = resolveMember(id);
          if (member?.kind === "team") return member.team.teamName;
          return (member?.kind === "user" ? member.user?.name : null) ?? "Unknown member";
        }
        case "outcome":
          return id === NO_OUTCOME_KEY
            ? "No outcome"
            : (reasonMap.get(id)?.name ?? "Unknown outcome");
        case "none":
          return "Projects";
      }
    };

    const colorOf = (id: string): Color | null => {
      if (id === UNASSIGNED_DIMENSION_KEY) return null;
      switch (groupBy) {
        case "stage":
          return (stageMap.get(id)?.icon?.color as Color | undefined) ?? null;
        case "type":
          return (issueTypeMap.get(id)?.icon?.color as Color | undefined) ?? null;
        case "workflow":
          return (workflowMap.get(id)?.icon?.color as Color | undefined) ?? null;
        case "team":
          return (teamMap.get(id)?.icon?.color as Color | undefined) ?? null;
        case "assignee": {
          // A user has no owned color (avatars, not icons); a team assignee
          // wears its team color.
          const member = resolveMember(id);
          return member?.kind === "team"
            ? ((member.team.icon?.color as Color | undefined) ?? null)
            : null;
        }
        case "outcome": {
          // An outcome's color is its TONE (good/neutral/bad), not an icon color.
          const tone = reasonMap.get(id)?.tone;
          return tone === undefined ? null : OUTCOME_TONE_CONFIG[normalizeOutcomeTone(tone)].swatch;
        }
        case "source":
        case "none":
          return null;
      }
    };

    const perSeries = new Map<string, { y: number[]; count: number[] }>();
    for (const c of cells) {
      const i = indexByKey.get(c.dateStr);
      if (i == null) continue;
      let arr = perSeries.get(c.seriesId);
      if (!arr) {
        arr = { y: new Array(axis.length).fill(0), count: new Array(axis.length).fill(0) };
        perSeries.set(c.seriesId, arr);
      }
      // Zero-filled to the axis length, so these slots already hold numbers.
      arr.y[i] = (arr.y[i] ?? 0) + c.y;
      arr.count[i] = (arr.count[i] ?? 0) + c.count;
    }

    const built = [...perSeries.entries()].map(([id, perBucket]) => {
      let running = 0;
      let runningCount = 0;
      const points = axis.map((b, i) => {
        const y = perBucket.y[i] ?? 0;
        const count = perBucket.count[i] ?? 0;
        running += y;
        runningCount += count;
        return {
          t: b.t,
          label: b.label,
          y: accumulate ? running : y,
          count: accumulate ? runningCount : count,
        };
      });
      return { id, label: labelOf(id), color: colorOf(id), points };
    });

    // Busiest lines first; keep the top few so the plot stays legible.
    built.sort(
      (a, b) =>
        b.points.reduce((sum, p) => sum + p.y, 0) - a.points.reduce((sum, p) => sum + p.y, 0),
    );
    return built.slice(0, MAX_SERIES);
  }, [
    counts,
    values,
    fn,
    fieldMeasures,
    bucket,
    range,
    groupBy,
    accumulate,
    stageMap,
    issueTypeMap,
    workflowMap,
    teamMap,
    issueSourceMap,
    reasonMap,
    resolveMember,
    customFields,
  ]);

  return { series, bucket, loading: !pending && !ready, error };
}
